import { forwardRef, memo, type MouseEventHandler, type Component } from 'react';
import clsx from 'clsx';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import DialogActions, { type DialogActionsProps } from '@mui/material/DialogActions';
// SCSS imports
import { backButton, confirmButton, actions, border } from './SkillLevelDialogActions.module.scss';

type SkillLevelDialogActionsProps = {
  readonly onConfirm: MouseEventHandler<HTMLButtonElement>;
  readonly onBack?: MouseEventHandler<HTMLButtonElement>;
  readonly disabled?: boolean | null;
  readonly pending?: boolean | null;
  readonly visible?: boolean | null;
  readonly withBorder?: boolean;
  readonly readOnly?: boolean;
  readonly sx?: DialogActionsProps['sx'];
  readonly backLabel?: string | null;
  readonly confirmLabel?: string | null;
}

const SkillLevelDialogActions = forwardRef<Component<DialogActionsProps>, SkillLevelDialogActionsProps>(({
  onConfirm,
  onBack,
  disabled = false,
  pending = false,
  visible = false,
  withBorder = false,
  readOnly = false,
  sx,
  backLabel,
  confirmLabel
}, ref) => {
  const confirmLabelId = confirmLabel || (readOnly ? 'common.button.close' : 'common.button.confirm');
  const backLabelId = backLabel || 'common.button.back';
  return (
    <DialogActions
        ref={ref}
        sx={sx}
        disableSpacing
        className={clsx({
          [actions]: !withBorder && visible,
          [border]: withBorder && visible
        })}
    >
      {visible ? (
        <>
          {onBack ? (
            <Button
                onClick={onBack}
                color="primary"
                variant="outlined"
                className={backButton}
            >
              <FormattedMessage id={backLabelId} defaultMessage={backLabelId}/>
            </Button>
          ) : undefined}
          <Button
              onClick={onConfirm}
              color="primary"
              variant="contained"
              disableElevation
              size="large"
              disabled={(disabled || pending) && !readOnly ? true : undefined}
              startIcon={pending ? <CircularProgress size={18} color="inherit"/> : undefined}
              className={confirmButton}
          >
            <FormattedMessage id={confirmLabelId} defaultMessage={confirmLabelId}/>
          </Button>
        </>
      ) : undefined}
    </DialogActions>
  )
});

SkillLevelDialogActions.displayName = 'SkillLevelDialogActions';

export default memo(SkillLevelDialogActions);
